import React, { useContext, useState } from "react";
import assetsManager from "../assets/assetsmanager/assestsmanager";
import { AppContext } from "AdminSection/context/context";
import { getAppMonitoringData } from "AdminSection/hooks/getAppMonitoringData";

const ApiMonitor = () => {
  const { state } = useContext(AppContext);
  const [selectedApi, setSelectedApi] = useState("/stream");
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState("1");
  const [isIntervalDropdownOpen, setIsIntervalDropdownOpen] = useState(false);
  const [metricsData, setMetricsData] = useState({
    totalRequests: 0,
    avgDuration: 0,
    successRate: 0
  });

  const apis = ["/stream", "/qna", "/bing_stream", "/bing_qna", "/fetch_nudge", "/make_nudge", "/nudge_qna", "/feedback", "/get_conversation_history"];

  const intervals = [
    { label: "Last 1 Hour", value: "1" },
    { label: "Last 2 Hours", value: "2" },
    { label: "Last 6 Hours", value: "6" },
    { label: "Last 12 Hours", value: "12" },
    { label: "Last 24 Hours", value: "24" },
    { label: "Last 48 Hours", value: "48" },
    { label: "Last 72 Hours", value: "72" }
  ];

  const handleFetch = async () => {
    try {
      const response = await getAppMonitoringData({
        route: selectedApi,
        hour: selectedInterval
      });
      console.log(
        `Fetching metrics for API: ${selectedApi} with interval: ${selectedInterval}`
      );
      setMetricsData({
        totalRequests: response?.total_requests || 0,
        avgDuration: response?.avg_duration || 0,
        successRate: response?.success_rate || 0
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  };

  return (
    <div className="h-[calc(100%_-_56px)] pb-2 mt-2">
      <div className="flex items-center justify-between h-[32px]">
        <p className="text-lg font-medium text-[#424242]">List of APIs</p>
      </div>

      <div className="flex flex-col gap-4 h-[calc(100%_-_56px)]">
        <div className="flex gap-2">
          {/* API Dropdown */}
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full bg-gray-100 border border-gray-200 rounded-md px-4 py-2 text-left text-sm text-gray-700 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="truncate">{selectedApi}</span>
              <span
                className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""
                  }`}
              >
                ▼
              </span>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {apis.map((api, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedApi(api);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700"
                  >
                    {api}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Time Interval Dropdown */}
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setIsIntervalDropdownOpen(!isIntervalDropdownOpen)}
              className="w-full bg-gray-100 border border-gray-200 rounded-md px-4 py-2 text-left text-sm text-gray-700 flex items-center justify-between hover:bg-gray-50"
            >
              <span>
                {
                  intervals.find((item) => item.value === selectedInterval)
                    ?.label
                }
              </span>
              <span
                className={`transition-transform ${isIntervalDropdownOpen ? "rotate-180" : ""
                  }`}
              >
                ▼
              </span>
            </button>

            {isIntervalDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {intervals.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => {
                      setSelectedInterval(item.value);
                      setIsIntervalDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700 "
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className={`px-4 py-2 rounded text-white text-sm transition ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#1C4EB2] hover:bg-[#163a8a]"
              }`}
            onClick={handleFetch}
            disabled={loading}
          >
            {loading ? "Loading..." : "Fetch"}
          </button>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Requests Card */}
          <div
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            style={{
              backgroundImage: `url(${assetsManager.cardBgImage})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right",
              backgroundSize: "contain"
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <img
                src={assetsManager.totalRequests}
                alt=""
                className="w-5 h-5"
              />
              <span className="text-sm text-gray-600 font-medium">
                Total Requests
              </span>
            </div>
            <div className="text-3xl font-semibold text-red-600">
              {metricsData.totalRequests.toLocaleString()}
            </div>
          </div>

          {/* Average Duration Card */}
          <div
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            style={{
              backgroundImage: `url(${assetsManager.cardBgImage})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right",
              backgroundSize: "contain"
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <img src={assetsManager.avgDuration} alt="" className="w-5 h-5" />
              <span className="text-sm text-gray-600 font-medium">
                Average Duration
              </span>
            </div>
            <div className="text-3xl font-semibold text-red-600">
              {metricsData.avgDuration.toFixed(2)}
            </div>
          </div>

          {/* Success Rate Card */}
          <div
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            style={{
              backgroundImage: `url(${assetsManager.cardBgImage})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right",
              backgroundSize: "contain"
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <img src={assetsManager.successRate} alt="" className="w-5 h-5" />
              <span>Success Rate</span>
            </div>
            <div className="text-3xl font-semibold text-red-600">
              {metricsData.successRate}%
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close any dropdown */}
      {(isDropdownOpen || isIntervalDropdownOpen) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setIsDropdownOpen(false);
            setIsIntervalDropdownOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ApiMonitor;
